/**
 * CloudLobes — the TURRET CLUSTER that gives a mesh cloud its silhouette
 * (2026-09-10, "the cloud meshes are all pill-shaped, not natural").
 *
 * A cloud volume used to be ONE flattened ellipsoid with noise carved inside
 * it and a radial boundary guard that followed the ellipsoid — so every
 * outline was a convex lozenge whatever the noise did. Real cumulus are
 * clusters of turrets: a shared FLAT base at the condensation level, several
 * rounded lobes of different sizes side by side, the largest tower in the
 * middle, smaller cauliflower risers sprouting from the big one (the WMO
 * atlas: "sprouting portions called turrets … crenelated with dozens of
 * lesser tuft-like units"; congestus "cauliflower-like"). The classic
 * modelling answer (Gardner's textured ellipsoids, Ebert / Nishita–Dobashi
 * metaballs) is exactly that: a union of spheroids defining the compact
 * support, noise doing the detail.
 *
 * So a cloud is now K spheroids in SHAPE SPACE — the unit ball the mesh
 * scales into world metres, with the FLAT BASE through its equator (y = 0 is
 * the condensation level, y = 1 the highest top; the lower half is cut). A
 * base through the equator is what gives a wide flat underside — a sphere
 * whose bottom is a point (the old bound) can hold no flat-based turret off
 * its axis. Each lobe (x, y, z, r) rests on the base (y = r) so bases stay
 * flat; tops vary with r; the union is a smooth-min SDF that the shader's
 * boundary guard follows instead of the radial distance. Deterministic from the cell hashes, so both the near
 * volumetric mesh and the far impostor sprite of one cloud share one
 * layout. Pure; unit-tested; the CPU `lobeUnionSdf` mirrors the shader.
 */
import type { CloudLayerKind } from './CloudMeshField.js';

export interface CloudLobe { x: number; y: number; z: number; r: number }

/** Fixed lobe slots per cloud (unused slots carry r = 0). */
export const MAX_LOBES = 7;
/** Smooth-min blend width of the union (shape space) — wide enough to FUSE
 *  neighbouring turrets into one lumpy mass instead of a cluster of balls. */
export const LOBE_SMIN_K = 0.28;
/** A grounded lobe's centre height as a fraction of its radius (see cloudLobes). */
export const LOBE_SEAT = 0.35;
const SEAT = LOBE_SEAT;

/** Polynomial smooth min (Quilez). */
export function smin(a: number, b: number, k: number): number {
  const h = Math.max(k - Math.abs(a - b), 0) / k;
  return Math.min(a, b) - h * h * k * 0.25;
}

/**
 * Turret layout for a cloud. `h` = six hash values in [0, 1) (the cell's
 * salts 1..6 — the same numbers the field already draws for size, altitude,
 * seed and character), `type01` = weather.g (humilis → congestus), `kind` =
 * the genus. Always returns MAX_LOBES entries (r = 0 pads).
 */
export function cloudLobes(h: number[], type01: number, kind: CloudLayerKind = 'cumulus'): CloudLobe[] {
  const H = (i: number): number => h[i % h.length] ?? 0.5;
  const t = Math.max(0, Math.min(1, type01));
  const out: CloudLobe[] = [];
  // A grounded lobe is centred BELOW its radius (c.y = SEAT·r) so the base
  // plane y = 0 cuts it along a wide chord — a flat-bottomed dome, not a ball
  // touching the ground at a point (from below, point-seated spheres read as
  // a bunch of balloons). Inside the unit ball: |c| + r ≤ LIMIT with
  // |c|² = d² + (SEAT·r)² — the quadratic below solves for the largest r.
  const LIMIT = 0.985;
  const restingR = (d: number, want: number): number => {
    const a = 1 - SEAT * SEAT; // 0.8775
    const disc = LIMIT * LIMIT - a * (LIMIT * LIMIT - d * d);
    if (disc < 0) return 0;
    return Math.min(want, (LIMIT - Math.sqrt(disc)) / a);
  };
  // How many satellites by genus: cumulus 3–5 (+ risers), stratocumulus 4–5
  // (a joined lumpy deck), altocumulus 2–3 (small separate cloudlets), cirrus
  // 2 (a streak is one long lobe plus a wisp).
  const satCount = kind === 'cirrus' ? 2 : kind === 'altocumulus' ? 2 + Math.round(H(0)) : 3 + Math.round(H(0) * 2);
  // Main tower: a little off-centre, the largest lobe; congestus (t → 1)
  // towers taller, humilis stays a low dome.
  const mx = (H(2) - 0.5) * 0.2, mz = (H(3) - 0.5) * 0.2;
  const R0 = restingR(Math.hypot(mx, mz), 0.44 + 0.1 * t + 0.04 * (H(1) - 0.5));
  out.push({ x: mx, y: SEAT * R0, z: mz, r: R0 });
  // Satellites: spread around the compass with jitter, resting on the base,
  // smaller toward the rim so the outline steps down like real turrets.
  for (let i = 0; i < satCount; i++) {
    const ang = (i / satCount) * Math.PI * 2 + (H(4 + i) - 0.5) * 1.1 + H(5) * 6.283;
    const dist = 0.28 + 0.38 * H(1 + 2 * i);
    let want = (0.22 + 0.28 * H(2 + 3 * i)) * (1 - 0.3 * dist);
    if (kind === 'stratocumulus') want *= 1.25; // joined lumps
    if (kind === 'cirrus') want *= 0.7;
    const r = restingR(dist, want);
    if (r < 0.1) continue;
    out.push({ x: Math.cos(ang) * dist, y: SEAT * r, z: Math.sin(ang) * dist, r });
  }
  // Cauliflower risers budding off the top and shoulder of the main tower
  // (two on the bigger types).
  if (kind === 'cumulus' || kind === 'stratocumulus') {
    const risers = t > 0.35 ? 2 : 1;
    for (let i = 0; i < risers; i++) {
      const a = H(3 + i) * 6.283;
      const off = R0 * (0.35 + 0.25 * H(5 + i));
      const cx = mx + Math.cos(a) * off, cz = mz + Math.sin(a) * off;
      const cy = SEAT * R0 + R0 * (0.72 + 0.2 * t);
      const want = R0 * (0.36 + 0.16 * H(4 + i));
      const rMax = LIMIT - Math.hypot(cx, cy, cz);
      if (rMax < 0.08) continue;
      out.push({ x: cx, y: cy, z: cz, r: Math.min(want, rMax) });
    }
  }
  while (out.length > MAX_LOBES) out.pop();
  while (out.length < MAX_LOBES) out.push({ x: 0, y: -10, z: 0, r: 0 });
  return out;
}

/** Signed distance (shape space) to the smooth union of the lobes — the CPU mirror of the shader guard. */
export function lobeUnionSdf(lobes: CloudLobe[], x: number, y: number, z: number, k = LOBE_SMIN_K): number {
  let d = Infinity;
  for (const l of lobes) {
    if (l.r <= 0) continue;
    const dl = Math.hypot(x - l.x, y - l.y, z - l.z) - l.r;
    d = d === Infinity ? dl : smin(d, dl, k);
  }
  return d;
}

/**
 * Radial extent of the union's silhouette (shape space) in horizontal
 * direction `ang` at height `y` (0 = base, 1 = top): the farthest point
 * along the ray where the union SDF ≤ 0. 0 when the ray never enters. Test helper — it is what makes
 * "not a pill" measurable.
 */
export function silhouetteRadius(lobes: CloudLobe[], ang: number, y: number, step = 0.01): number {
  let last = 0;
  for (let s = 0; s <= 1.2; s += step) {
    if (lobeUnionSdf(lobes, Math.cos(ang) * s, y, Math.sin(ang) * s) <= 0) last = s;
  }
  return last;
}
